/* ==========================================================================
   MP — Announcement Bar
   Section : sections/mp-announcement-bar.liquid
   Styles  : assets/mp-announcement-bar.css

   Owns three jobs:

   1. CYCLING. Own implementation, not Swiper. Swiper is on this project's
      hazard list (it sets height:100% inside flex chains and needs explicit
      height:auto pins on WebKit) and a stack of 2-6 text rows does not need
      a 140 KB carousel library.

   2. THE TWO GLOBAL HEIGHT VARIABLES. This is the part that is easy to miss.
      --ann-height and --announcement-height are published BY the announcement
      bar and consumed all over the theme: the mobile menu drawer, predictive
      search, the header overlay, and every full-height slideshow. The stock
      bar published them from assets/ann-bar.js, which is loaded from inside
      the stock section — so replacing that section removes the only code that
      sets them. base.css `.header__overlay { top: var(--ann-height) }` has no
      fallback value, so an undefined --ann-height makes `top` compute to auto
      and detaches the overlay. Hence: this file republishes both.

   3. DISMISSAL. The pre-paint check lives inline in the section (it has to run
      before first paint). This file only handles the click.

   Build stamp: VERSION below must match --mp-annbar-v declared in the CSS.
   The check crosses a file boundary on purpose — a push that lands one file
   and not the other warns in the console instead of failing silently.
   ========================================================================== */
(function () {
  'use strict';

  var VERSION = '20260930a';
  var STORE_KEY = 'mp-annbar';

  /* IntersectionObserver thresholds. The stock ann-bar.js used 1000 steps for
     an element ~50px tall — 0.05px of granularity, far past anything visible.
     101 steps gives ~0.5px on the same element at a fraction of the callback
     cost. */
  var THRESHOLDS = (function () {
    var out = [];
    for (var i = 0; i <= 100; i += 1) out.push(i / 100);
    return out;
  })();

  var docEl = document.documentElement;

  /* Live instances. Document-level listeners are registered ONCE against this
     list rather than once per bar — the theme editor re-renders a section on
     every settings change, and a per-instance document listener would stack up
     a new copy each time with no way to remove the old one. */
  var instances = [];
  var docBound = false;

  function bindDocumentListeners() {
    if (docBound) return;
    docBound = true;

    /* A background tab throttles timers to roughly once a minute, which would
       stack up rotations and land the bar on an arbitrary message on return. */
    document.addEventListener('visibilitychange', function () {
      var hidden = document.visibilityState !== 'visible';
      for (var i = 0; i < instances.length; i += 1) {
        instances[i].setVisibilityPause(hidden);
      }
    });

    /* Reduced motion can be toggled mid-session. */
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onChange = function () {
      for (var i = 0; i < instances.length; i += 1) instances[i].onMotionChange();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  function setVar(name, value) {
    docEl.style.setProperty(name, value);
  }

  function zeroHeights() {
    setVar('--ann-height', '0px');
    setVar('--announcement-height', '0px');
  }

  function init(root) {
    if (!root) return;
    if (root.dataset.mpAnnbarReady === '1') return;
    root.dataset.mpAnnbarReady = '1';

    /* ---- build stamp ------------------------------------------------- */
    var cssVersion = getComputedStyle(root).getPropertyValue('--mp-annbar-v').trim();
    root.setAttribute('data-mp-annbar-v', VERSION);
    if (cssVersion !== VERSION) {
      console.warn(
        '[mp-annbar] build stamp mismatch — js ' +
          VERSION +
          ' vs css "' +
          cssVersion +
          '". One of the two files did not land. Measurements taken now are not trustworthy.'
      );
    } else {
      console.log('[mp-annbar] ' + VERSION);
    }

    /* ---- element refs ------------------------------------------------ */
    var section = root.closest('.section-announcement') || root;

    /* Floating bars are fixed-position: they take no room out of the page
       flow, so both height globals must stay at 0 for them. See publishHeights
       and startObservers below, and the block comment in the section.

       Read HERE rather than down in the config block: publishHeights() is
       called from the no-messages early return above that block, and a `var`
       read before its assignment is undefined, which would have made an empty
       floating bar take the inline path. */
    var isFloating = root.dataset.display === 'floating';
    if (isFloating && section !== root) {
      section.classList.add('mp-annbar-section--floating');
    }
    var controls = root.querySelector('.mp-annbar__controls');
    var viewport = root.querySelector('.mp-annbar__viewport');
    var msgs = Array.prototype.slice.call(root.querySelectorAll('.mp-annbar__msg'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.mp-annbar__dot'));
    var progressFill = root.querySelector('.mp-annbar__progress-fill');
    var closeBtn = root.querySelector('.mp-annbar__close');
    var prevBtn = root.querySelector('.mp-annbar__arrow--prev');
    var nextBtn = root.querySelector('.mp-annbar__arrow--next');

    if (!msgs.length) {
      publishHeights();
      return;
    }

    /* ---- config ------------------------------------------------------ */
    var mode = root.dataset.cycle || 'auto';
    var dwell = (parseFloat(root.dataset.dwell) || 5) * 1000;
    var speed = (parseFloat(root.dataset.speed) || 0.5) * 1000;
    var pauseOnHover = root.dataset.pauseOnHover === 'true';
    var dismissMemory = root.dataset.dismissMemory || 'content';
    var signature = root.dataset.signature || '';

    var baseBg = root.dataset.baseBg || '';
    var baseFg = root.dataset.baseFg || '';

    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var autoAllowed = (mode === 'auto' || mode === 'auto_and_manual') && msgs.length > 1;

    var index = 0;
    var timer = null;
    var leaveTimer = null;
    var progressAnim = null;
    var pausedByHover = false;
    var pausedByFocus = false;
    var pausedByVisibility = false;

    /* ==================================================================
       Height variables
       ================================================================== */
    var io = null;
    var ro = null;
    var roDebounce = null;

    /* Last intersection ratio seen, so a RESIZE can republish --ann-height
       without waiting for the observer.

       Found 2026-09-29 by measuring across an orientation change. --ann-height
       is height x ratio and was only ever written from the IntersectionObserver
       callback. IO fires on THRESHOLD crossings, not on size changes: a bar
       that is fully visible stays at ratio 1 while its height changes, so no
       callback fires and the old height is left behind. The stock bar never hit
       this because its height was a fixed px value; this one is content-sized
       and grows, so rotating a phone from portrait (two lines, 64px) to
       landscape (one line, 40px) stranded --ann-height 24px too large and threw
       off .header__overlay and the mobile menu drawer.

       --announcement-height was already republished by the ResizeObserver. The
       asymmetry was the bug. */
    var lastRatio = 1;

    /* Measure the BAR, not the section wrapper.

       base.css:2797 sizes the wrapper FROM --announcement-height. Measuring the
       wrapper therefore feeds this function its own previous output, and any
       moment the value is 0 (a section re-render in the theme editor zeroes it)
       the wrapper collapses, the next measurement reads 0, and the bar is stuck
       invisible forever — undo cannot recover it because nothing was saved.
       Observed 2026-09-12 in the editor. The bar's own box is content-sized and
       has no such dependency.

       The 0 guard is the second half: while a bar is on the page its height is
       never legitimately 0, so refusing to publish 0 makes the loop unreachable
       even if some other rule collapses the wrapper again. Dismissal zeroes the
       variables explicitly and does not go through here. */
    function publishHeights() {
      /* A floating bar occupies no flow. Publishing its real height would
         shorten the mobile menu drawer, the predictive-search panel and every
         100vh section by a bar that is not above them. */
      if (isFloating) {
        setVar('--announcement-height', '0px');
        return;
      }
      var h = root.getBoundingClientRect().height;
      if (h <= 0) return;
      setVar('--announcement-height', h.toFixed(2) + 'px');
    }

    function publishAnnHeight() {
      if (isFloating) {
        setVar('--ann-height', '0px');
        return;
      }
      var h = root.getBoundingClientRect().height;
      if (h <= 0) return;
      setVar('--ann-height', (h * lastRatio).toFixed(2) + 'px');
    }

    function publishControlWidth() {
      if (!controls) return;
      /* Centre layout pads the message box by this much on BOTH sides so the
         message stays optically centred in the bar and can never slide under
         the close button. Scoped to this bar, not the document. */
      root.style.setProperty(
        '--mp-annbar-ctrl-w',
        controls.getBoundingClientRect().width.toFixed(2) + 'px'
      );
    }

    function startObservers() {
      /* --ann-height answers "how much of the bar is still between the top of
         the window and the header". A floating bar is never in that position,
         so the answer is a constant 0 and there is nothing to observe. */
      if (isFloating) {
        setVar('--ann-height', '0px');
        startResizeObserver();
        return;
      }

      /* --ann-height tracks the bar sliding away under the sticky header, so
         it is height x intersectionRatio, not a resting height. Ported from
         the stock assets/ann-bar.js so the drawer and search panels keep the
         exact behaviour they were written against. */
      io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i += 1) {
            var entry = entries[i];
            if (entry.isIntersecting) {
              lastRatio = entry.intersectionRatio;
              publishAnnHeight();
            } else {
              lastRatio = 0;
              setVar('--ann-height', '0px');
            }
          }
        },
        { threshold: THRESHOLDS }
      );
      io.observe(section);
      startResizeObserver();
    }

    function startResizeObserver() {
      if ('ResizeObserver' in window) {
        /* Debounced with setTimeout, not rAF: background and throttled
           contexts suspend rAF delivery and would strand a stale height. */
        ro = new ResizeObserver(function () {
          clearTimeout(roDebounce);
          roDebounce = setTimeout(function () {
            publishHeights();
            publishAnnHeight();
            publishControlWidth();
          }, 60);
        });
        ro.observe(section);
        if (controls) ro.observe(controls);
        /* Keep the reference on the element — a bare local can be collected
           in some engines while the element is still live. */
        root.mpAnnbarResizeObserver = ro;
      } else {
        window.addEventListener('resize', function () {
          clearTimeout(roDebounce);
          roDebounce = setTimeout(function () {
            publishHeights();
            publishAnnHeight();
            publishControlWidth();
          }, 120);
        });
      }
    }

    /* ==================================================================
       Cycling
       ================================================================== */
    function setActive(next, dir) {
      if (next === index || msgs.length < 2) return;

      root.setAttribute('data-dir', dir === 'rev' ? 'rev' : 'fwd');

      var outgoing = msgs[index];
      var incoming = msgs[next];

      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-leaving');
      outgoing.setAttribute('aria-hidden', 'true');

      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');

      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(
        function () {
          outgoing.classList.remove('is-leaving');
        },
        motionQuery.matches ? 10 : speed
      );

      index = next;
      applyColors(incoming);
      syncDots();
    }

    /* A per-message colour override repaints the WHOLE bar, not just the
       message box — a background that only covered the text would leave the
       bar's own padding in the old colour and read as a rendering fault.
       Falls back to the section colours when a message sets none. */
    function applyColors(msg) {
      if (!msg) return;
      root.style.setProperty('--mp-annbar-bg', msg.getAttribute('data-bg') || baseBg);
      root.style.setProperty('--mp-annbar-fg', msg.getAttribute('data-fg') || baseFg);
    }

    function syncDots() {
      for (var i = 0; i < dots.length; i += 1) {
        dots[i].setAttribute('aria-current', i === index ? 'true' : 'false');
      }
    }

    function advance(step, dir) {
      var next = (index + step + msgs.length) % msgs.length;
      setActive(next, dir);
    }

    /* ---- autoplay ----------------------------------------------------- */
    function isPaused() {
      return pausedByHover || pausedByFocus || pausedByVisibility;
    }

    function runProgress() {
      if (!progressFill || !progressFill.animate) return;
      if (progressAnim) progressAnim.cancel();
      progressAnim = progressFill.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: dwell, easing: 'linear', fill: 'forwards' }
      );
    }

    function tick() {
      advance(1, 'fwd');
      schedule();
    }

    function schedule() {
      clearTimeout(timer);
      if (!autoAllowed || motionQuery.matches || isPaused()) return;
      runProgress();
      timer = setTimeout(tick, dwell);
    }

    function pause() {
      clearTimeout(timer);
      timer = null;
      if (progressAnim && progressAnim.playState === 'running') progressAnim.pause();
    }

    function resume() {
      if (!autoAllowed || motionQuery.matches || isPaused()) return;
      if (progressAnim && progressAnim.playState === 'paused') {
        progressAnim.play();
        /* Finish the remainder of the current dwell rather than a fresh one. */
        var remaining = dwell;
        try {
          remaining = Math.max(0, dwell - (progressAnim.currentTime || 0));
        } catch (e) {
          /* currentTime can throw on a cancelled animation — fall back to a
             full dwell rather than firing immediately. */
        }
        clearTimeout(timer);
        timer = setTimeout(tick, remaining);
      } else {
        schedule();
      }
    }

    /* ---- manual interaction resets the clock -------------------------- */
    function manual(step, dir) {
      advance(step, dir);
      schedule();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        manual(-1, 'rev');
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        manual(1, 'fwd');
      });
    }

    /* ---- swipe --------------------------------------------------------
       The arrow pair is hidden below 750px by default (stylesheet section 11:
       in the centre layout a control is reserved on BOTH sides of the message,
       so an arrow pair costs twice its own width in text room). Swipe is what
       replaces it, so manual rotation still exists on a phone.

       Three things this deliberately does NOT do:

       - It does not preventDefault. The CSS sets `touch-action: pan-y` on the
         viewport, which tells the engine up front that vertical scrolling is
         native and horizontal is ours. Cancelling the event instead would mean
         guessing the visitor's intent from the first few pixels and getting it
         wrong on a diagonal flick.
       - It does not fire on a gesture that is more vertical than horizontal.
         A page scroll started on top of the bar must scroll the page.
       - It does not fire on a tap. The 40px floor is above thumb jitter, so
         tapping the close button or the CTA cannot also change the message.

       Bound on the viewport rather than the root so a swipe that starts on the
       close button is not read as navigation. */
    if (viewport && root.classList.contains('mp-annbar--m-swipe')) {
      var swipeX = 0;
      var swipeY = 0;
      var swiping = false;

      viewport.addEventListener(
        'touchstart',
        function (event) {
          if (event.touches.length !== 1) {
            swiping = false;
            return;
          }
          swiping = true;
          swipeX = event.touches[0].clientX;
          swipeY = event.touches[0].clientY;
          pausedByHover = true;
          pause();
        },
        { passive: true }
      );

      viewport.addEventListener(
        'touchend',
        function (event) {
          if (!swiping) return;
          swiping = false;
          pausedByHover = false;

          var touch = event.changedTouches && event.changedTouches[0];
          if (!touch) {
            resume();
            return;
          }

          var dx = touch.clientX - swipeX;
          var dy = touch.clientY - swipeY;

          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) {
            resume();
            return;
          }

          /* Swiping left moves forward, matching every native pager. */
          manual(dx < 0 ? 1 : -1, dx < 0 ? 'fwd' : 'rev');
        },
        { passive: true }
      );

      viewport.addEventListener(
        'touchcancel',
        function () {
          if (!swiping) return;
          swiping = false;
          pausedByHover = false;
          resume();
        },
        { passive: true }
      );
    }

    for (var d = 0; d < dots.length; d += 1) {
      (function (i) {
        dots[i].addEventListener('click', function () {
          setActive(i, i > index ? 'fwd' : 'rev');
          schedule();
        });
      })(d);
    }

    if (pauseOnHover) {
      root.addEventListener('mouseenter', function () {
        pausedByHover = true;
        pause();
      });
      root.addEventListener('mouseleave', function () {
        pausedByHover = false;
        resume();
      });
    }

    /* Focus pause is NOT optional and is not tied to the hover setting — an
       auto-rotating region that moves while a keyboard user is tabbing
       through its links is the accessibility failure, and hover cannot cover
       it. */
    root.addEventListener('focusin', function () {
      pausedByFocus = true;
      pause();
    });
    root.addEventListener('focusout', function () {
      if (!root.contains(document.activeElement)) {
        pausedByFocus = false;
        resume();
      }
    });

    /* Visibility and reduced-motion are driven from the shared document
       listeners via this handle (see bindDocumentListeners). */
    var handle = {
      root: root,
      setVisibilityPause: function (hidden) {
        pausedByVisibility = hidden;
        if (hidden) pause();
        else resume();
      },
      onMotionChange: function () {
        if (motionQuery.matches) pause();
        else schedule();
      },
    };
    instances.push(handle);
    bindDocumentListeners();

    /* ==================================================================
       Dismissal
       ================================================================== */
    function store() {
      try {
        return dismissMemory === 'session' ? window.sessionStorage : window.localStorage;
      } catch (e) {
        return null;
      }
    }

    function dismiss() {
      var s = store();
      if (s) {
        try {
          s.setItem(STORE_KEY, signature + '|' + Date.now());
        } catch (e) {
          /* Private mode / quota. The bar still hides for this pageview. */
        }
      }

      docEl.classList.add('mp-annbar-hidden');
      /* Set explicitly rather than waiting for an observer — the element is
         display:none from this moment, so neither observer will fire again
         and a stale height would be left behind for the drawer to use. */
      zeroHeights();

      pause();
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      teardown();
    }

    function teardown() {
      var at = instances.indexOf(handle);
      if (at !== -1) instances.splice(at, 1);
    }

    root.mpAnnbarTeardown = teardown;

    if (closeBtn) closeBtn.addEventListener('click', dismiss);

    /* ==================================================================
       Entrance animation

       The CSS describes the BEFORE state; adding `is-entered` releases it.
       Two frames of delay so the before state is painted first — a class
       added in the same frame as the element's first layout produces no
       transition at all, it just appears.

       rAF is suspended in a background tab, which would strand an invisible
       bar until the tab is foregrounded. The setTimeout is the floor: timers
       are throttled in the background but they do still fire.
       ================================================================== */
    if (root.classList.contains('mp-annbar--enter')) {
      var entered = false;
      var enter = function () {
        if (entered) return;
        entered = true;
        root.classList.add('is-entered');
      };
      if (motionQuery.matches) {
        enter();
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(enter);
        });
        setTimeout(enter, 120);
      }
    }

    /* ==================================================================
       Close after the link is used

       Delegated from the root rather than bound per anchor, because the
       anchors live inside the rotating messages and a per-anchor listener
       would have to be rebound every time the markup is re-rendered in the
       theme editor. Navigation is not prevented: the dismissal is written
       synchronously, which completes before the document unloads, and for a
       new-tab link the visitor stays here and watches the bar close.
       ================================================================== */
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('[data-cta-close]')) return;
      dismiss();
    });

    /* ==================================================================
       Go
       ================================================================== */
    msgs[0].classList.add('is-active');
    for (var m = 1; m < msgs.length; m += 1) {
      msgs[m].setAttribute('aria-hidden', 'true');
    }
    root.setAttribute('data-dir', 'fwd');
    applyColors(msgs[0]);
    syncDots();

    startObservers();
    publishHeights();
    publishControlWidth();
    schedule();

    /* One deferred re-measure: web fonts swapping in can change the bar's
       height after first layout, and on a text-only bar that is the single
       most likely source of a wrong --announcement-height. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        publishHeights();
        publishAnnHeight();
        publishControlWidth();
      });
    }
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('.mp-annbar');
    for (var i = 0; i < nodes.length; i += 1) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor: sections are re-rendered on every settings change. The
     per-element ready flag lives on the new DOM, so a re-render initialises
     cleanly and cannot double-bind. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;
    var gone = event.target.querySelectorAll('.mp-annbar');
    if (!gone.length) return;
    for (var i = 0; i < gone.length; i += 1) {
      if (typeof gone[i].mpAnnbarTeardown === 'function') gone[i].mpAnnbarTeardown();
      if (gone[i].mpAnnbarResizeObserver) gone[i].mpAnnbarResizeObserver.disconnect();
    }
    /* An unload in the theme editor is usually the first half of a RE-RENDER,
       not a removal — shopify:section:load follows immediately with fresh DOM.
       Zeroing straight away collapsed the bar mid-swap. Check on the next tick
       whether a bar actually survived, and only zero if the section is really
       gone. `.section-announcement` matches the stock bar too, so publishing a
       0 here also collapses that one — one bug, two symptoms. */
    setTimeout(function () {
      if (!document.querySelector('.mp-annbar')) zeroHeights();
    }, 0);
  });
})();
